import {
  BookmarkSquareIcon,
  DocumentDuplicateIcon,
  NoSymbolIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  CloudDoneOutlined,
  CloudUploadOutlined,
  CopyAllOutlined,
  EventRepeatOutlined,
  ShareOutlined,
} from "@mui/icons-material";
import { Chip, Stack, Typography } from "@mui/joy";
import { PaymentFlowTypeEnum, OrderStatusEnum } from "../../types/order";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { showSuccessToast, SuccessToastConfig } from "../Error";
import { MouseEventHandler, useEffect, useState } from "react";
import { BtnTonalDangerSm, BtnTonalPrimarySm } from "../stateless/buttons";

interface OrderHeaderProps {
  orderType: string;
  currentOrderStatus: string;
  revisions: Array<{ [key: string]: any }>;
  invoiceNumber: string | null;
  paymentLink?: { [key: string]: any } | null;
  autoSaveLoading?: boolean;
  isCreator?: boolean;
  isAdjustmentOrder?: boolean;
  toPDF?: any;
  handleDraftSave?: any;
  handleDeleteDraftOrder?: any;
  handleCancelAdjustment?: any;
  handleCancel?: any;
  handleGeneratePdf?: MouseEventHandler<HTMLAnchorElement>;
  orderHeaderActionButtons?: JSX.Element;
}

export default function OrderHeader({
  orderType,
  currentOrderStatus,
  revisions,
  invoiceNumber,
  autoSaveLoading,
  isCreator,
  isAdjustmentOrder,
  toPDF,
  handleDraftSave,
  handleDeleteDraftOrder,
  handleCancelAdjustment,
  handleCancel,

  // handleGeneratePdf,
  paymentLink,
}: OrderHeaderProps) {
  const { t } = useTranslation();
  const { getValues } = useFormContext();

  const recurringDay = getValues("recurringDay");
  const startMonthYear = dayjs(getValues("startMonth"));
  const today = dayjs();
  const definedRecurringDate =
    recurringDay && startMonthYear.date(recurringDay);

  const isFromCatalog = getValues("isFromCatalog");
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  useEffect(() => {
    console.log("GENPDF", pdfFile);
    if (
      (invoiceNumber && toPDF && !pdfFile) ||
      (pdfFile && pdfFile?.size / 1024 < 100)
    )
      toPDF().then((pdfObject: any) => {
        console.log("GENPDF", invoiceNumber);
        const blob = pdfObject.output("blob");
        const file = new File([blob], `${invoiceNumber}-order.pdf`, {
          type: "application/pdf",
        });
        setPdfFile(file);
      });
  }, [invoiceNumber, toPDF, pdfFile]);

  const handleGeneratePdf = async () => {
    if (pdfFile) {
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (navigator?.canShare({ files: [pdfFile] }) && isMobile) {
        console.log("can share", pdfFile);
        navigator?.share({ files: [pdfFile] });
      } else {
        const url = URL.createObjectURL(pdfFile);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${invoiceNumber}-order.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    }

    // toPDF().then((pdfObject: jsPDF) => {
    //   const blob = pdfObject.output("blob");
    //   const file = new File([blob], `${invoiceNumber}-order.pdf`, {
    //     type: "application/pdf",
    //   });
    //   if (navigator?.canShare({ files: [file] })) {
    //     console.log("can share", file);
    //     navigator?.share({ files: [file] });
    //   } else {
    //     pdfObject.save(`${invoiceNumber}-order.pdf`);
    //   }
    //   setIsGeneratingPDF(false);
    // });
  };

  const ConditionalHeaderActionButtons = () => {
    if (isCreator) {
      if (currentOrderStatus === OrderStatusEnum.DRAFT) {
        return (
          <>
            <BtnTonalPrimarySm
              startDecorator={<BookmarkSquareIcon width={20} height={20} />}
              onClick={() => handleDraftSave()}
              label={t("order.createOrder.form.saveOrderBtn")}
            />
            <BtnTonalDangerSm
              startDecorator={<TrashIcon width={20} height={20} />}
              onClick={() => {
                // If adjustment order, when delete draft, revert the data to latest revision version
                isAdjustmentOrder
                  ? handleCancelAdjustment()
                  : handleDeleteDraftOrder();
              }}
              label={t("order.createOrder.deleteDraftBtn")}
            />
          </>
        );
      } else if (currentOrderStatus === OrderStatusEnum.AWAITING_RESPONSE) {
        return (
          <>
            <BtnTonalPrimarySm
              startDecorator={<BookmarkSquareIcon width={20} height={20} />}
              onClick={() => handleDraftSave()}
              label={t("order.createOrder.form.saveOrderBtn")}
            />
            <BtnTonalDangerSm
              startDecorator={<NoSymbolIcon width={20} height={20} />}
              onClick={
                isAdjustmentOrder ? handleCancelAdjustment : handleCancel
              }
              label={t("order.createOrder.cancelOrderBtn")}
            />
          </>
        );
      } else if (
        ![OrderStatusEnum.COMPLETE, OrderStatusEnum.CANCELLED].includes(
          currentOrderStatus as OrderStatusEnum
        )
      ) {
        return (
          <BtnTonalDangerSm
            startDecorator={<NoSymbolIcon width={20} height={20} />}
            onClick={isAdjustmentOrder ? handleCancelAdjustment : handleCancel}
            label={t("order.createOrder.cancelOrderBtn")}
          />
        );
      } else {
        return <></>;
      }
    }
    return <></>;
  };

  return (
    <Stack>
      <Stack
        direction={"row"}
        gap={"8px"}
        alignItems={"center"}
        flexWrap={"wrap"}
      >
        {/* Order Type Chip */}
        <Chip
          variant="soft"
          sx={{
            "--Chip-radius": "8px",
            color: isFromCatalog
              ? "var(--joy-palette-others-order_catalogue_text)"
              : `var(--joy-palette-others-order_${orderType}_text)`,
            background: isFromCatalog
              ? "var(--joy-palette-others-order_catalogue)"
              : `var(--joy-palette-others-order_${orderType})`,
          }}
        >
          {isFromCatalog ? "Catalogue Order" : `${orderType} Order`}
        </Chip>

        {/* Order Number */}
        <Stack
          direction={"row"}
          gap={"8px"}
          alignItems={"center"}
          flexWrap={"wrap"}
        >
          <Typography
            level={"text-xl-semibold"}
            startDecorator={
              getValues("paymentFlowType") ===
              PaymentFlowTypeEnum.RECURRING_PAYMENT ? (
                <Stack
                  borderRadius={"8px"}
                  bgcolor={"var(--joy-palette-primary-100)"}
                  width={28}
                  height={28}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <EventRepeatOutlined color="primary" />
                </Stack>
              ) : revisions?.length > 0 ? (
                <Stack
                  borderRadius={"8px"}
                  bgcolor={"#F7D5AC"}
                  width={28}
                  height={28}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <DocumentDuplicateIcon
                    width={20}
                    height={20}
                    color="#D48434"
                  />
                </Stack>
              ) : (
                ""
              )
            }
          >
            {t("order.createOrder.summary.orderNumber")}:
          </Typography>

          {/* Generate Invoice Number Once Awaiting Response occurred */}
          <Typography
            level={"text-xl-semibold"}
            textColor={!invoiceNumber ? "#D0D5DD" : ""}
          >
            {invoiceNumber || "000000-0000000"}
          </Typography>
        </Stack>
      </Stack>

      {/* Header Buttons Row */}
      <Stack
        flex={1}
        direction={"row"}
        gap={"8px"}
        alignItems={"center"}
        flexWrap={"wrap"}
        marginTop={"16px"}
      >
        {/** Share PDF */}
        {toPDF && currentOrderStatus !== OrderStatusEnum.DRAFT && (
          <BtnTonalPrimarySm
            startDecorator={<ShareOutlined />}
            disabled={!pdfFile || (pdfFile && pdfFile.size / 1024 < 100)}
            onClick={handleGeneratePdf}
            label={
              !pdfFile || (pdfFile && pdfFile.size / 1024 < 100)
                ? "Preparing ..."
                : t("order.createOrder.summary.sharePdfBtn")
            }
          />
        )}

        {/** Payment Link */}
        {paymentLink &&
          (getValues("paymentFlowType") !==
            PaymentFlowTypeEnum.RECURRING_PAYMENT ||
            today.isAfter(definedRecurringDate)) && (
            // && getValues("paymentFlowType") !==
            // PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
            <BtnTonalPrimarySm
              startDecorator={<CopyAllOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(paymentLink?.url);
                toast(
                  showSuccessToast(
                    t("order.createOrder.summary.paymentLinkCopied")
                  ),
                  SuccessToastConfig
                );
              }}
              label={t("order.createOrder.summary.copyPaymentLinkBtn")}
            />
          )}

        {/** Order Header Action Buttons - JSX Element */}
        {ConditionalHeaderActionButtons()}

        {/** Auto Draft Save Loading */}
        {autoSaveLoading &&
          (autoSaveLoading ? (
            <Typography
              startDecorator={<CloudUploadOutlined />}
              fontWeight={600}
              pl={0.6}
            >
              {t("order.createOrder.summary.draftSaving")}
            </Typography>
          ) : (
            <Typography
              color="primary"
              startDecorator={<CloudDoneOutlined />}
              fontWeight={600}
              pl={0.6}
            >
              {t("order.createOrder.summary.draftSaved")}
            </Typography>
          ))}
      </Stack>
    </Stack>
  );
}
